import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  FormControlLabel,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon, PlayArrow as PlayArrowIcon } from '@mui/icons-material';
import { observer } from 'mobx-react-lite';
import { getSnapshot } from 'mobx-state-tree';
import { v4 } from 'uuid';
import { IAsset, useStore } from '@mcn-platform/models';
import ReferenceTagsInput from 'components/forms/TagsInput/ReferenceTagsInput';
import AssetPickerDialog from 'components/asset/AssetPickerDialog';

const SYNC_FORMATS = [
  { value: 'json', label: 'JSON' },
  { value: 'buyblvd_graphql', label: 'Buy Blvd (GraphQL)' },
  { value: 'xml', label: 'XML' },
  { value: 'csv', label: 'CSV' },
  { value: 'xlsx', label: 'Excel (XLSX)' },
  { value: 'kibo', label: 'Kibo' },
  { value: 'bigcommerce', label: 'BigCommerce' },
  { value: 'essense', label: 'Essense' },
];

const SPREADSHEET_FEED_MIME_TYPES = new Set([
  'text/csv',
  'text/tab-separated-values',
  'application/csv',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel.sheet.macroEnabled.12',
  'application/vnd.oasis.opendocument.spreadsheet',
  'application/vnd.apple.numbers',
  'application/x-iwork-numbers-sffnumbers',
]);

const SPREADSHEET_FEED_FILE_EXT = /\.(csv|tsv|xlsx?|xlsm|ods)$/i;

const SPREADSHEET_FEED_MIME_HINT = /spreadsheet|excel|\.sheet|csv|tab-separated|numbers/i;

const SPREADSHEET_FEED_ACCEPT = [
  '.csv',
  '.tsv',
  '.xlsx',
  '.xls',
  '.ods',
  'text/csv',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel.sheet.macroEnabled.12',
  'application/vnd.oasis.opendocument.spreadsheet',
].join(',');

const SPREADSHEET_FEED_LIST_FILTER = {
  or: [
    { type: { eq: 'application' } },
    { type: { eq: 'text' } },
    // Browser uploads with missing MIME often land as type `any` until reprocessed.
    { type: { eq: 'any' } },
  ],
};

const isSpreadsheetFeedAsset = (asset: Pick<IAsset, 'mimeType' | 'name' | 'type'>): boolean => {
  const mime = (asset.mimeType ?? '').toLowerCase();
  if (SPREADSHEET_FEED_MIME_TYPES.has(mime) || SPREADSHEET_FEED_MIME_HINT.test(mime)) {
    return true;
  }
  const name = asset.name ?? '';
  if (SPREADSHEET_FEED_FILE_EXT.test(name)) {
    return true;
  }
  // Generic application/octet-stream uploads: rely on filename extension.
  if (
    (asset.type === 'application' || asset.type === 'text' || asset.type === 'any')
    && (!mime || mime === 'application/octet-stream')
  ) {
    return SPREADSHEET_FEED_FILE_EXT.test(name);
  }
  return false;
};

type SyncConfigAssetPickerProps = {
  assetID?: string;
  name: string;
  label: string;
  helperText?: string;
  listFilter?: Record<string, unknown>;
  itemFilter?: (asset: IAsset) => boolean;
  acceptType?: string;
  assetType?: 'any' | 'application';
  onChange: (asset: IAsset | null) => void;
};

const SyncConfigAssetPicker = ({
  assetID,
  name,
  label,
  helperText,
  listFilter,
  itemFilter,
  acceptType,
  assetType = 'any',
  onChange,
}: SyncConfigAssetPickerProps) => (
  <Stack spacing={0.5}>
    <InputLabel shrink sx={{ position: 'static', transform: 'none' }}>
      {label}
    </InputLabel>
    <Box
      sx={{
        width: '100%',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        p: 2,
      }}
    >
      <AssetPickerDialog
        value={assetID}
        name={name}
        canUpload
        showSelectedImage
        assetType={assetType}
        listFilter={listFilter}
        itemFilter={itemFilter}
        acceptType={acceptType}
        onChange={onChange}
      />
    </Box>
    {helperText && (
      <Typography variant="caption" color="text.secondary">
        {helperText}
      </Typography>
    )}
  </Stack>
);

export type SyncConfig = {
  id: string;
  format: string;
  name?: string;
  url?: string;
  assetID?: string;
  /** Library IDs; may be a legacy comma-separated string in stored JSON */
  libraryKeys?: string[] | string;
  hostName?: string;
  storeHash?: string;
  postData?: Record<string, unknown>;
  groupingField?: string;
  /** XLSX: import only these sheet names (omit for all sheets) */
  xlsxSheetNames?: string[];
  /** XLSX: 0-based row index of the header row on each sheet (default 0) */
  xlsxHeaderRowIndex?: number;
  storeProductAdditionalData?: boolean;
  jsonFormat?: string;
  /** Buy Blvd Catalog API — prefer SSM `buyblvdCatalogApiConfig/{config id}` with apiToken */
  graphqlUrl?: string;
  retailerId?: string;
  brandId?: string;
  pageSize?: number;
  apiToken?: string;
  extractIdentifierRules?: Array<{ field: string; level: string }>;
  /** When true, scheduled product feeds skip this config (manual run also skipped). */
  inactive?: boolean;
  [key: string]: unknown;
};

interface OrganizationSyncConfigsManagerProps {
  item?: { id: string; syncConfigs?: SyncConfig[]; updateValues: (v: Record<string, unknown>) => void };
}

const parseSyncConfigs = (value: unknown): SyncConfig[] => {
  if (!value) return [];
  try {
    const arr = typeof value === 'string' ? JSON.parse(value) : value;
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
};

const libraryKeyToString = (k: unknown): string => {
  if (k == null) return '';
  if (typeof k === 'string') return k.trim();
  if (typeof k === 'object' && k !== null && 'id' in k) {
    const id = (k as { id?: unknown }).id;
    return id != null ? String(id).trim() : '';
  }
  return String(k).trim();
};

const normalizeLibraryKeys = (v: string[] | string | undefined): string[] => {
  if (Array.isArray(v)) return v.map(libraryKeyToString).filter(Boolean);
  if (typeof v === 'string') return v.split(/[,;]\s*/).map((s) => s.trim()).filter(Boolean);
  return [];
};

/** Derive a human-readable sync config name from an asset filename. */
const syncConfigNameFromAssetName = (assetName: string): string => {
  const trimmed = assetName.trim();
  if (!trimmed) return '';
  const withoutExt = trimmed.replace(/\.[^.]+$/, '').trim();
  return withoutExt || trimmed;
};

const OrganizationSyncConfigsManager: React.FC<OrganizationSyncConfigsManagerProps> = ({
  item: organization,
}) => {
  const store = useStore();
  // Read from snapshot to handle MST observables; fallback to direct access
  const syncConfigsRaw = organization
    ? (getSnapshot(organization as Parameters<typeof getSnapshot>[0]) as { syncConfigs?: unknown })
        ?.syncConfigs ?? organization?.syncConfigs
    : undefined;
  const configs = parseSyncConfigs(syncConfigsRaw ?? []);

  // Reload organization when this tab is shown so we always have fresh syncConfigs
  // (handles case where org was loaded from userInfo before syncConfigs existed)
  useEffect(() => {
    if (organization?.id && typeof (store as any).load === 'function') {
      (store as any).load('organization', organization.id, 0);
    }
  }, [organization?.id, store]);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [syncLoading, setSyncLoading] = useState(false);
  const [runningConfigId, setRunningConfigId] = useState<string | null>(null);
  const [fullLoad, setFullLoad] = useState(false);
  const [editingConfig, setEditingConfig] = useState<SyncConfig | null>(null);
  const [formConfig, setFormConfig] = useState<Partial<SyncConfig>>({});

  const updateConfigs = useCallback(
    (newConfigs: SyncConfig[]) => {
      organization?.updateValues?.({ syncConfigs: newConfigs });
    },
    [organization],
  );

  const handleAdd = () => {
    const defaultLibraryId = libraryKeyToString(store.currentPortal?.defaultLibrary);
    const defaultLibraryKeys = defaultLibraryId ? [defaultLibraryId] : [];
    setFormConfig({
      id: `sync-${v4().slice(0, 8)}`,
      format: 'json',
      libraryKeys: defaultLibraryKeys,
    });
    setEditingConfig(null);
  };

  const handleEdit = (config: SyncConfig) => {
    setFormConfig({ ...config, libraryKeys: normalizeLibraryKeys(config.libraryKeys) });
    setEditingConfig(config);
  };

  const handleFeedAssetChange = useCallback((asset: IAsset | null) => {
    setFormConfig((prev) => {
      const next: Partial<SyncConfig> = {
        ...prev,
        assetID: asset?.id ?? undefined,
        ...(asset?.id ? { url: undefined } : {}),
      };
      if (asset?.name && !String(prev.name ?? '').trim()) {
        next.name = syncConfigNameFromAssetName(asset.name);
      }
      return next;
    });
  }, []);

  const handleSave = () => {
    const cfg = formConfig as SyncConfig;
    if (!cfg.id || !cfg.format) return;
    const libKeys = normalizeLibraryKeys(cfg.libraryKeys);
    const normalized = { ...cfg, libraryKeys: libKeys };
    const current = parseSyncConfigs(organization?.syncConfigs ?? []);
    let next: SyncConfig[];
    if (editingConfig) {
      next = current.map((c) => (c.id === editingConfig.id ? normalized : c));
    } else {
      if (current.some((c) => c.id === normalized.id)) {
        setSyncError(`Config with ID "${normalized.id}" already exists.`);
        return;
      }
      next = [...current, normalized];
    }
    updateConfigs(next);
    setEditingConfig(null);
    setFormConfig({});
  };

  const handleDelete = (config: SyncConfig) => {
    const current = parseSyncConfigs(organization?.syncConfigs ?? []);
    updateConfigs(current.filter((c) => c.id !== config.id));
  };

  const handleProductFeedSync = async (syncConfigID?: string) => {
    if (!organization?.id) return;
    const isSingle = Boolean(syncConfigID);
    if (isSingle) {
      setRunningConfigId(syncConfigID ?? null);
    } else {
      setSyncLoading(true);
    }
    setSyncMessage(null);
    setSyncError(null);
    try {
      const message = await store.runProductFeed(organization.id, fullLoad, syncConfigID);
      setSyncMessage(message);
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Failed to start product feed sync';
      if (isSingle && msg.includes('Unknown field argument syncConfigID')) {
        setSyncError(
          'Per-config feed run requires a backend deploy (amplify push) with the updated runProductFeed schema. Use Run All Feeds after deploy, or deploy first.',
        );
      } else {
        setSyncError(msg);
      }
    } finally {
      if (isSingle) {
        setRunningConfigId(null);
      } else {
        setSyncLoading(false);
      }
    }
  };

  const formatLibraryKeysForTable = (keys: string[] | string | undefined) => {
    const list = normalizeLibraryKeys(keys);
    if (!list.length) return '—';
    return list
      .map((k) => {
        const lib = store.getOrLoad('library', k);
        return lib?.name ? `${lib.name} (${k})` : k;
      })
      .join(', ');
  };

  const getAssetDisplayName = (assetID: string | undefined): string | undefined => {
    if (!assetID) return undefined;
    const asset = store.getOrLoad('asset', assetID);
    return asset?.name?.trim() || undefined;
  };

  const getSourceDisplay = (c: SyncConfig) => {
    if (['xlsx', 'csv'].includes(c.format ?? '')) {
      if (c.name?.trim()) return c.name.trim();
      const assetName = getAssetDisplayName(c.assetID);
      if (assetName) return assetName;
      if (c.url) return c.url;
      if (c.assetID) return c.assetID;
      return '—';
    }
    if (c.url) return c.url;
    if (c.assetID) {
      const assetName = getAssetDisplayName(c.assetID);
      return assetName ? assetName : `Asset: ${c.assetID}`;
    }
    if (c.format === 'buyblvd_graphql') {
      return c.graphqlUrl || 'https://gql.buyblvd.com/graphql';
    }
    if (c.hostName) return c.hostName;
    if (c.storeHash) return `Store: ${c.storeHash}`;
    return '—';
  };

  const isDialogOpen = editingConfig !== null || Object.keys(formConfig).length > 0;

  if (!organization) return null;

  return (
    <Grid container direction="row" spacing={1}>
      <Grid item xs={12}>
        <Typography variant="h4" component="div">
          Organization Sync Configs
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Product feed configs for JSON, Buy Blvd GraphQL, XML, CSV, Excel (XLSX), Kibo, BigCommerce, and Essense. Shopify configs are managed separately on each Shopify Config.
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Box
          sx={{
            width: '100%',
            border: '1px solid',
            borderColor: '#7f7f7f',
            borderRadius: 1,
            padding: 2,
          }}
        >
          <Stack spacing={2}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <FormControlLabel
                control={
                  <Switch
                    checked={fullLoad}
                    onChange={(e) => setFullLoad(e.target.checked)}
                    disabled={syncLoading}
                  />
                }
                label="Full Load"
              />
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleAdd}
                >
                  Add Config
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => handleProductFeedSync()}
                  disabled={syncLoading || runningConfigId !== null}
                >
                  {syncLoading ? 'Running...' : 'Run All Feeds'}
                </Button>
              </Stack>
            </Stack>

            {syncMessage && <Alert severity="success">{syncMessage}</Alert>}
            {syncError && <Alert severity="error">{syncError}</Alert>}

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Format</TableCell>
                    <TableCell>Active</TableCell>
                    <TableCell>Source / URL</TableCell>
                    <TableCell>Library Keys</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {configs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ color: 'text.secondary', py: 3 }}>
                        No sync configs. Click Add Config to create one.
                      </TableCell>
                    </TableRow>
                  ) : (
                    configs.map((c) => (
                      <TableRow key={c.id}>
                        <TableCell>{c.id}</TableCell>
                        <TableCell>{c.format}</TableCell>
                        <TableCell>{c.inactive ? 'No' : 'Yes'}</TableCell>
                        <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {getSourceDisplay(c)}
                        </TableCell>
                        <TableCell>
                          {formatLibraryKeysForTable(c.libraryKeys)}
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={() => handleProductFeedSync(c.id)}
                            aria-label={`Run feed ${c.id}`}
                            disabled={syncLoading || runningConfigId !== null}
                          >
                            {runningConfigId === c.id
                              ? <CircularProgress size={18} />
                              : <PlayArrowIcon fontSize="small" />}
                          </IconButton>
                          <IconButton size="small" onClick={() => handleEdit(c)} aria-label="Edit">
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => handleDelete(c)} aria-label="Delete" color="error">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        </Box>
      </Grid>

      <Dialog open={!!isDialogOpen} onClose={() => { setEditingConfig(null); setFormConfig({}); }} maxWidth="sm" fullWidth>
        <DialogTitle>{editingConfig ? 'Edit Sync Config' : 'Add Sync Config'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Config ID"
              value={formConfig.id ?? ''}
              onChange={(e) => setFormConfig((prev) => ({ ...prev, id: e.target.value }))}
              required
              fullWidth
              size="small"
              helperText="Unique identifier for this config (e.g. buyblvd, discountdance)"
              disabled={!!editingConfig}
            />
            <FormControl fullWidth size="small" required>
              <InputLabel>Format</InputLabel>
              <Select
                value={formConfig.format ?? 'json'}
                label="Format"
                onChange={(e) => {
                  const format = e.target.value;
                  setFormConfig((prev) => ({
                    ...prev,
                    format,
                    ...(format !== 'xlsx'
                      ? { xlsxSheetNames: undefined, xlsxHeaderRowIndex: undefined }
                      : {}),
                  }));
                }}
              >
                {SYNC_FORMATS.map((f) => (
                  <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Name (optional)"
              value={formConfig.name ?? ''}
              onChange={(e) => setFormConfig((prev) => ({ ...prev, name: e.target.value }))}
              fullWidth
              size="small"
            />
            <FormControlLabel
              control={(
                <Switch
                  checked={formConfig.inactive !== true}
                  onChange={(e) => setFormConfig((prev) => ({
                    ...prev,
                    inactive: e.target.checked ? undefined : true,
                  }))}
                />
              )}
              label="Active (scheduled feeds run when enabled)"
            />
            {formConfig.format === 'json' && (
              <>
                <TextField
                  label="URL"
                  value={formConfig.url ?? ''}
                  onChange={(e) => setFormConfig((prev) => ({ ...prev, url: e.target.value || undefined }))}
                  fullWidth
                  size="small"
                  placeholder="https://..."
                  helperText="Feed URL (or select an uploaded JSON asset below)"
                />
                <SyncConfigAssetPicker
                  assetID={formConfig.assetID}
                  name="sync-config-json-asset"
                  label="JSON asset (optional)"
                  helperText="Select or upload a JSON feed file instead of using a URL."
                  onChange={handleFeedAssetChange}
                />
              </>
            )}
            {['xml', 'essense'].includes(formConfig.format ?? '') && (
              <TextField
                label="URL"
                value={formConfig.url ?? ''}
                onChange={(e) => setFormConfig((prev) => ({ ...prev, url: e.target.value || undefined }))}
                fullWidth
                size="small"
                placeholder="https://..."
                helperText="Feed URL"
                required
              />
            )}
            {formConfig.format === 'csv' && (
              <>
                <SyncConfigAssetPicker
                  assetID={formConfig.assetID}
                  name="sync-config-csv-asset"
                  label="CSV / spreadsheet asset"
                  helperText="Select or upload a CSV, TSV, or Excel file. You can use a URL instead if preferred."
                  listFilter={SPREADSHEET_FEED_LIST_FILTER}
                  itemFilter={isSpreadsheetFeedAsset}
                  acceptType={SPREADSHEET_FEED_ACCEPT}
                  assetType="application"
                  onChange={handleFeedAssetChange}
                />
                <TextField
                  label="URL (optional)"
                  value={formConfig.url ?? ''}
                  onChange={(e) => setFormConfig((prev) => ({
                    ...prev,
                    url: e.target.value || undefined,
                    ...(e.target.value.trim() ? { assetID: undefined } : {}),
                  }))}
                  fullWidth
                  size="small"
                  placeholder="https://..."
                  helperText="Use a public CSV/TSV URL instead of an uploaded asset."
                />
              </>
            )}
            {formConfig.format === 'xlsx' && (
              <>
                <SyncConfigAssetPicker
                  assetID={formConfig.assetID}
                  name="sync-config-xlsx-asset"
                  label="Excel / CSV asset"
                  helperText="Select or upload an .xlsx, .xls, or .csv file. You can use a URL instead if preferred."
                  listFilter={SPREADSHEET_FEED_LIST_FILTER}
                  itemFilter={isSpreadsheetFeedAsset}
                  acceptType={SPREADSHEET_FEED_ACCEPT}
                  assetType="application"
                  onChange={handleFeedAssetChange}
                />
                <TextField
                  label="URL (optional)"
                  value={formConfig.url ?? ''}
                  onChange={(e) => setFormConfig((prev) => ({
                    ...prev,
                    url: e.target.value || undefined,
                    ...(e.target.value.trim() ? { assetID: undefined } : {}),
                  }))}
                  fullWidth
                  size="small"
                  placeholder="https://..."
                  helperText="Use a public spreadsheet URL instead of an uploaded asset."
                />
                <TextField
                  label="Sheet names (optional)"
                  value={
                    Array.isArray(formConfig.xlsxSheetNames)
                      ? formConfig.xlsxSheetNames.join(', ')
                      : ''
                  }
                  onChange={(e) => {
                    const raw = e.target.value.trim();
                    const names = raw
                      ? raw.split(/[,;]\s*/).map((s) => s.trim()).filter(Boolean)
                      : undefined;
                    setFormConfig((prev) => ({
                      ...prev,
                      xlsxSheetNames: names && names.length > 0 ? names : undefined,
                    }));
                  }}
                  fullWidth
                  size="small"
                  placeholder="Sheet1, Sheet2 — blank imports every sheet"
                />
                <TextField
                  label="Header row index"
                  value={
                    formConfig.xlsxHeaderRowIndex != null
                      ? String(formConfig.xlsxHeaderRowIndex)
                      : ''
                  }
                  onChange={(e) => {
                    const raw = e.target.value.trim();
                    if (!raw) {
                      setFormConfig((prev) => ({ ...prev, xlsxHeaderRowIndex: undefined }));
                      return;
                    }
                    const n = parseInt(raw, 10);
                    setFormConfig((prev) => ({
                      ...prev,
                      xlsxHeaderRowIndex: Number.isFinite(n) && n >= 0 ? n : undefined,
                    }));
                  }}
                  fullWidth
                  size="small"
                  placeholder="0"
                  helperText="First row of column titles on each sheet (0 = first row)"
                />
              </>
            )}
            {(formConfig.format === 'kibo' || formConfig.format === 'bigcommerce') && (
              <>
                <TextField
                  label="Host Name"
                  value={formConfig.hostName ?? ''}
                  onChange={(e) => setFormConfig((prev) => ({ ...prev, hostName: e.target.value }))}
                  fullWidth
                  size="small"
                  placeholder="www.example.com or api.example.com"
                />
                {formConfig.format === 'bigcommerce' && (
                  <TextField
                    label="Store Hash"
                    value={formConfig.storeHash ?? ''}
                    onChange={(e) => setFormConfig((prev) => ({ ...prev, storeHash: e.target.value }))}
                    fullWidth
                    size="small"
                    placeholder="BigCommerce store hash"
                  />
                )}
              </>
            )}
            {formConfig.format === 'buyblvd_graphql' && (
              <>
                <TextField
                  label="GraphQL URL (optional)"
                  value={formConfig.graphqlUrl ?? ''}
                  onChange={(e) => setFormConfig((prev) => ({ ...prev, graphqlUrl: e.target.value || undefined }))}
                  fullWidth
                  size="small"
                  placeholder="https://gql.buyblvd.com/graphql"
                  helperText="Defaults to gql.buyblvd.com. Auth: SSM buyblvdCatalogApiConfig/{config id} (JSON apiToken) or apiToken below for local testing only."
                />
                <TextField
                  label="Retailer ID (optional)"
                  value={formConfig.retailerId ?? ''}
                  onChange={(e) => setFormConfig((prev) => ({ ...prev, retailerId: e.target.value || undefined }))}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Brand ID (optional)"
                  value={formConfig.brandId ?? ''}
                  onChange={(e) => setFormConfig((prev) => ({ ...prev, brandId: e.target.value || undefined }))}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Page size (1–100)"
                  value={formConfig.pageSize != null ? String(formConfig.pageSize) : ''}
                  onChange={(e) => {
                    const raw = e.target.value.trim();
                    if (!raw) {
                      setFormConfig((prev) => ({ ...prev, pageSize: undefined }));
                      return;
                    }
                    const n = parseInt(raw, 10);
                    setFormConfig((prev) => ({
                      ...prev,
                      pageSize: Number.isFinite(n) ? n : undefined,
                    }));
                  }}
                  fullWidth
                  size="small"
                  placeholder="50"
                />
              </>
            )}
            <Stack spacing={0.5}>
              <InputLabel id="sync-config-libraries-label" shrink sx={{ position: 'static', transform: 'none' }}>
                Libraries
              </InputLabel>
              <ReferenceTagsInput
                id="sync-config-libraries"
                name="libraryKeys"
                aria-labelledby="sync-config-libraries-label"
                placeholder="Search and add libraries…"
                value={normalizeLibraryKeys(formConfig.libraryKeys)}
                onChange={(keys: string[]) => {
                  setFormConfig((prev) => ({ ...prev, libraryKeys: keys }));
                }}
                modelName="library"
                disabled={false}
                size="small"
              />
              <Typography variant="caption" color="text.secondary">
                Same multi-select as course Advanced Options → Libraries. Feed output is written to these libraries.
              </Typography>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setEditingConfig(null); setFormConfig({}); }}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={!formConfig.id || !formConfig.format}>
            {editingConfig ? 'Save' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default observer(OrganizationSyncConfigsManager);
